import React, { useState, useEffect, useMemo } from "react";
import { motion } from "framer-motion";
import { useCourse } from "../../../../context/CourseContext";
import {
  ChevronUp,
  ChevronDown,
  Search,
  ChevronLeft,
  ChevronRight,
  Filter,
} from "lucide-react";
import {
  card,
  microLabel,
  bodySecondary,
  accentBarSection,
  motionFadeUp,
  pageTitle,
} from "../../../../lib/design-system";
import { cn } from "../../../../lib/utils";

const StudentTable = () => {
  const { courseData, getStudentAttendanceRate } = useCourse();
  const [data, setData] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedProgram, setSelectedProgram] = useState("all");
  const [selectedBranch, setSelectedBranch] = useState("all");
  const [sortConfig, setSortConfig] = useState({
    key: "rollNo",
    direction: "asc",
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(100);

  // Extract unique programs from students
  const uniquePrograms = useMemo(() => {
    if (!courseData?.students) return [];
    const programs = [...new Set(courseData.students.map((s) => s.program).filter(Boolean))];
    return programs.sort();
  }, [courseData?.students]);

  // Check if we need to show program filter (only if multiple programs exist)
  const showProgramFilter = uniquePrograms.length > 1;

  // Extract unique branches based on selected program
  const uniqueBranches = useMemo(() => {
    if (!courseData?.students) return [];
    let studentsToFilter = courseData.students;

    if (selectedProgram !== "all") {
      studentsToFilter = studentsToFilter.filter((s) => s.program === selectedProgram);
    }

    const branches = [...new Set(studentsToFilter.map((s) => s.branch).filter(Boolean))];
    return branches.sort();
  }, [courseData?.students, selectedProgram]);

  // Reset branch filter when program changes
  useEffect(() => {
    setSelectedBranch("all");
  }, [selectedProgram]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, selectedProgram, selectedBranch, itemsPerPage]);

  // Helper: get attendance rate for a student based on current branch filter
  const getAttendance = (studentId) => {
    const branch = selectedBranch === "all" ? undefined : selectedBranch;
    return getStudentAttendanceRate(studentId, branch);
  };

  // Helper function for sorting
  const compareValues = (a, b, key, direction) => {
    if (key === "attendance") {
      const valA = getAttendance(a.id);
      const valB = getAttendance(b.id);
      const comparison = valA - valB;
      return direction === "asc" ? comparison : -comparison;
    }
    const valA = a[key] || "";
    const valB = b[key] || "";
    const comparison = valA.localeCompare(valB, undefined, { numeric: true });
    return direction === "asc" ? comparison : -comparison;
  };

  // Effect to load and sort data from context
  useEffect(() => {
    if (courseData?.students) {
      const sortedInitialData = [...courseData.students].sort((a, b) => {
        return compareValues(a, b, sortConfig.key, sortConfig.direction);
      });
      setData(sortedInitialData);
    }
  }, [courseData?.students]);

  // Sorting function
  const requestSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });

    const sortedData = [...data].sort((a, b) => {
      return compareValues(a, b, key, direction);
    });
    setData(sortedData);
  };

  // Filtering function (search + program + branch with AND logic)
  const filteredData = data.filter((item) => {
    const matchesSearch = Object.values(item).some((value) =>
      value?.toString().toLowerCase().includes(searchTerm.toLowerCase())
    );
    const matchesProgram = selectedProgram === "all" || item.program === selectedProgram;
    const matchesBranch = selectedBranch === "all" || item.branch === selectedBranch;
    return matchesSearch && matchesProgram && matchesBranch;
  });

  // Pagination
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredData.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredData.length / itemsPerPage);

  // Attendance color coding
  const getAttendanceColor = (pct) => {
    if (pct >= 75) return "text-accent1";
    if (pct >= 50) return "text-amber-500";
    return "text-red-500";
  };

  // Column headers with sorting
  const SortableHeader = ({ label, sortKey }) => (
    <th
      className={cn(
        microLabel,
        "px-4 py-3 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors text-left"
      )}
      onClick={() => requestSort(sortKey)}
    >
      <div className="flex items-center space-x-1">
        <span>{label}</span>
        {sortConfig.key === sortKey && (
          <div className="flex flex-col">
            <ChevronUp
              className={`w-4 h-4 ${
                sortConfig.direction === "asc"
                  ? "text-gray-900 dark:text-white"
                  : "text-gray-400 dark:text-gray-500"
              }`}
            />
            <ChevronDown
              className={`w-4 h-4 ${
                sortConfig.direction === "desc"
                  ? "text-gray-900 dark:text-white"
                  : "text-gray-400 dark:text-gray-500"
              }`}
            />
          </div>
        )}
      </div>
    </th>
  );

  const inputClasses =
    "rounded-sm border border-gray-200 dark:border-gray-700 focus:ring-2 focus:ring-accent1/50 focus:border-transparent bg-white dark:bg-gray-800 text-gray-900 dark:text-white";

  return (
    <motion.div {...motionFadeUp} className="p-4 sm:p-6 lg:p-8 space-y-6">
      {/* Page heading */}
      <div>
        <h1 className={pageTitle}>Class List</h1>
        <p className={cn(bodySecondary, "mt-1")}>
          {courseData?.students?.length || 0} students enrolled
        </p>
        <div className={cn(accentBarSection, "mt-3")} />
      </div>

      {/* Search Bar and Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        {/* Search Input */}
        <div className="relative flex-1">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Search className="w-5 h-5 text-gray-400 dark:text-gray-500" />
          </div>
          <input
            type="text"
            placeholder="Search students..."
            className={cn(inputClasses, "pl-10 w-full p-2 placeholder-gray-500 dark:placeholder-gray-400")}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        {/* Program Filter */}
        {showProgramFilter && (
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Filter className="w-4 h-4 text-gray-400 dark:text-gray-500" />
            </div>
            <select
              value={selectedProgram}
              onChange={(e) => setSelectedProgram(e.target.value)}
              className={cn(inputClasses, "pl-9 pr-8 py-2 appearance-none cursor-pointer min-w-[140px]")}
            >
              <option value="all">All Programs</option>
              {uniquePrograms.map((program) => (
                <option key={program} value={program}>
                  {program}
                </option>
              ))}
            </select>
            <div className="absolute inset-y-0 right-0 pr-2 flex items-center pointer-events-none">
              <ChevronDown className="w-4 h-4 text-gray-400 dark:text-gray-500" />
            </div>
          </div>
        )}

        {/* Branch Filter */}
        {uniqueBranches.length > 0 && (
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Filter className="w-4 h-4 text-gray-400 dark:text-gray-500" />
            </div>
            <select
              value={selectedBranch}
              onChange={(e) => setSelectedBranch(e.target.value)}
              className={cn(inputClasses, "pl-9 pr-8 py-2 appearance-none cursor-pointer min-w-[140px]")}
            >
              <option value="all">All Branches</option>
              {uniqueBranches.map((branch) => (
                <option key={branch} value={branch}>
                  {branch}
                </option>
              ))}
            </select>
            <div className="absolute inset-y-0 right-0 pr-2 flex items-center pointer-events-none">
              <ChevronDown className="w-4 h-4 text-gray-400 dark:text-gray-500" />
            </div>
          </div>
        )}
      </div>

      {/* Table */}
      <div className={cn(card, "overflow-x-auto")}>
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead className="bg-gray-50 dark:bg-gray-900">
            <tr>
              <th className={cn(microLabel, "px-4 py-3 text-left")}>Sl. No.</th>
              <SortableHeader label="Roll No." sortKey="rollNo" />
              <SortableHeader label="Name" sortKey="name" />
              <SortableHeader label="Email" sortKey="email" />
              <SortableHeader label="Attendance %" sortKey="attendance" />
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
            {currentItems.map((item, index) => {
              const attendance = Math.round(getAttendance(item.id));
              return (
                <tr key={item.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                  <td className="px-4 py-3 text-center text-gray-900 dark:text-white">
                    {indexOfFirstItem + index + 1}
                  </td>
                  <td className="px-4 py-3 text-gray-900 dark:text-white">{item.rollNo || "—"}</td>
                  <td className="px-4 py-3 text-gray-900 dark:text-white">{item.name}</td>
                  <td className="px-4 py-3 text-gray-900 dark:text-white">{item.email}</td>
                  <td className={cn("px-4 py-3 font-bold", getAttendanceColor(attendance))}>
                    {attendance}%
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <div className={cn("text-sm", bodySecondary)}>
            Showing {filteredData.length > 0 ? indexOfFirstItem + 1 : 0} to{" "}
            {Math.min(indexOfLastItem, filteredData.length)} of{" "}
            {filteredData.length} entries
          </div>
          <div className="flex items-center gap-2">
            <span className={cn("text-sm", bodySecondary)}>Show:</span>
            <select
              value={itemsPerPage}
              onChange={(e) => setItemsPerPage(Number(e.target.value))}
              className={cn(inputClasses, "px-2 py-1 text-sm cursor-pointer")}
            >
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
            disabled={currentPage === 1}
            className={cn(
              "p-2 rounded-sm border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed",
              bodySecondary
            )}
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <span className={cn("text-sm px-2", bodySecondary)}>
            Page {currentPage} of {totalPages || 1}
          </span>
          <button
            onClick={() =>
              setCurrentPage((prev) => Math.min(prev + 1, totalPages))
            }
            disabled={currentPage === totalPages || totalPages === 0}
            className={cn(
              "p-2 rounded-sm border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed",
              bodySecondary
            )}
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>
      </div>
    </motion.div>
  );
};

export default StudentTable;
